import { type NavigateFunction } from "react-router-dom";

import { SearchParams } from "@/shared/environment";
import { LoggerService } from "@/shared/services/LoggerService";
import { nanoSecToSecStr } from "@/shared/time";

import { layoutNavigationState } from "./layoutNavigation";
import {
  ActionRouteParams,
  ActionsRouteParam,
  CollectionRouteParams,
  DatasetRouteParams,
  DeviceRouteParams,
  FileRouteParams,
  InviteRouteParams,
  InvocationRouteParams,
  InvokeActionRouteParams,
  SignInRouteParams,
  SignUpRouteParams,
  TriggerRouteParams,
  VisualizerRouteParams,
} from "./types";

export class Goto {
  static route = Object.freeze({
    Account: "/account",
    ActionHub: "/actions/hub",
    Actions: "/actions",
    Admin: "/admin",
    Collections: "/collections",
    CreateAction: "/actions/create",
    CreateDataset: "/datasets/create",
    CreateTrigger: "/triggers/create",
    Datasets: "/datasets",
    Devices: "/devices",
    Files: "/files",
    FoxgloveIntegration: "/integrations/foxglove",
    Home: "/",
    Integrations: "/integrations",
    Invite: "/invites",
    Invocations: "/invocations",
    InvokeAction: "/actions/invoke",
    Notifications: "/notifications",
    Orgs: "/orgs",
    Search: "/search",
    Settings: "/settings",
    SignIn: "/signin",
    SignUp: "/signup",
    Triggers: "/triggers",
    Visualize: "/visualize",
    SecretsList: "/settings?tab=5",
  } as const);

  #navigate: NavigateFunction;

  constructor(navigator: NavigateFunction) {
    this.#navigate = navigator;
  }

  public account() {
    this.#navigate(Goto.route.Account);
  }

  public home() {
    this.#navigate(Goto.route.Home);
  }

  public devices() {
    this.#navigate(Goto.route.Devices);
  }

  public signIn(params: SignInRouteParams = {}) {
    const { inviteId, chooseOrg } = params;

    if (inviteId && chooseOrg) {
      LoggerService.warn(
        "Both inviteId and chooseOrg are set. Ignoring chooseOrg",
      );
    }

    if (inviteId) {
      this.#navigate(`${Goto.route.SignIn}?inviteId=${inviteId}`);
      return;
    }

    if (chooseOrg) {
      this.#navigate(`${Goto.route.SignIn}?chooseorg=true`);

      return;
    }

    this.#navigate(Goto.route.SignIn);
  }

  public signUp(params: SignUpRouteParams = {}) {
    const { interruptedVerificationCode } = params;

    if (interruptedVerificationCode) {
      this.#navigate(`${Goto.route.SignUp}?interruptedVerificationCode=true`);
    } else {
      this.#navigate(Goto.route.SignUp);
    }
  }

  public invite(params: InviteRouteParams = {}) {
    const { inviteId } = params;
    if (inviteId) {
      this.#navigate(`${Goto.route.Invite}/${inviteId}`);
    } else {
      this.#navigate(Goto.route.Invite);
    }
  }

  public search(urlParams?: URLSearchParams) {
    if (urlParams) {
      this.#navigate(`${Goto.route.Search}?${urlParams.toString()}`);
      return;
    }
    this.#navigate(Goto.route.Search);
  }

  public static createDatasetRoute(datasetId: string, directoryPath?: string) {
    if (directoryPath) {
      const encodedDirPath = directoryPath
        .split("/")
        .map(encodeURIComponent)
        .join("/");
      return `${Goto.route.Datasets}/${datasetId}/files/${encodedDirPath}`;
    }
    return `${Goto.route.Datasets}/${datasetId}`;
  }

  public dataset({ datasetId, directoryPath }: DatasetRouteParams) {
    const path = Goto.createDatasetRoute(datasetId, directoryPath);
    this.#navigate(path);
  }

  public datasetTopics({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/topics`);
  }

  public datasetEvents({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/events`);
  }

  public datasetActions({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/actions`);
  }

  public datasetDetails({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/details`);
  }

  public datasetPermissions({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/permissions`);
  }

  public createDataset() {
    this.#navigate(`${Goto.route.CreateDataset}`);
  }

  public createDatasetEvent({ datasetId }: DatasetRouteParams) {
    this.#navigate(`${Goto.route.Datasets}/${datasetId}/events/create`);
  }

  public actions(params: ActionsRouteParam = {}) {
    const { tab } = params;
    if (tab) {
      this.#navigate(`${Goto.route.Actions}?tab=${tab}`);
    } else {
      this.#navigate(Goto.route.Actions);
    }
  }

  public action({ action }: ActionRouteParams) {
    const urlParts: string[] = [Goto.route.Actions];
    if (action.owner) {
      urlParts.push(action.owner);
    }
    if (action.digest) {
      urlParts.push(`${action.name}?digest=${action.digest}`);
    } else {
      urlParts.push(action.name);
    }
    this.#navigate(urlParts.join("/"));
  }

  public createAction() {
    this.#navigate(Goto.route.CreateAction);
  }

  public invocation({ invocationId, tab }: InvocationRouteParams) {
    if (tab) {
      this.#navigate(`${Goto.route.Invocations}/${invocationId}?tab=${tab}`);
    } else {
      this.#navigate(`${Goto.route.Invocations}/${invocationId}`);
    }
  }

  public invokeAction(params: InvokeActionRouteParams = {}) {
    const { action, datasetId, baseInvocation, inputFiles } = params;
    const urlParams = new URLSearchParams();

    if (action) {
      // Add "action_digest" to query params when we want to
      // enable invoking a specific version of an action
      urlParams.append("action_name", action.name);
      if (action.owner) {
        urlParams.append("action_owner", action.owner);
      }
    }
    if (datasetId) {
      urlParams.append("dataset_id", datasetId);
    }
    if (baseInvocation) {
      urlParams.append("base_invocation", baseInvocation);
    }

    if (inputFiles) {
      const encodedInputFiles = inputFiles
        .map((item) => encodeURIComponent(item))
        .join(",");
      urlParams.append("input_files", encodedInputFiles);
    }

    const queryString = urlParams.toString();
    const href = queryString
      ? `${Goto.route.InvokeAction}?${urlParams.toString()}`
      : `${Goto.route.InvokeAction}`;

    this.#navigate(href);
  }

  public trigger({ triggerId }: TriggerRouteParams) {
    this.#navigate(`${Goto.route.Triggers}/${triggerId}`);
  }

  public createTrigger() {
    this.#navigate(Goto.route.CreateTrigger);
  }

  public collections() {
    this.#navigate(Goto.route.Collections);
  }

  public collection({ collectionId }: CollectionRouteParams) {
    this.#navigate(`${Goto.route.Collections}/${collectionId}`);
  }

  public device({ deviceId }: DeviceRouteParams) {
    this.#navigate(`${Goto.route.Devices}/${deviceId}`);
  }

  public file({ fileId, params, beforeNavigation }: FileRouteParams) {
    if (beforeNavigation !== undefined) {
      beforeNavigation();
    }

    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down

    if (params) {
      const { layout, t, ...rest } = params;
      const searchParams = new URLSearchParams(rest);

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }

      const hasSearchParams = searchParams.size > 0;

      this.#navigate(
        `${Goto.route.Files}/${fileId}${hasSearchParams ? `?${searchParams}` : ""}`,
      );
    } else {
      this.#navigate(`${Goto.route.Files}/${fileId}`);
    }
  }

  public visualize({ dataSource, params }: VisualizerRouteParams) {
    const searchParams = new URLSearchParams();

    if (dataSource.dataset) {
      searchParams.set("dataset", dataSource.dataset);
    }

    if (dataSource.files) {
      dataSource.files.forEach((fileId) => {
        searchParams.append("file", fileId);
      });
    }

    if (params) {
      const { layout, t, ...rest } = params;

      // Add other parameters (like filterTerm) to search params
      Object.entries(rest).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.set(key, String(value));
        }
      });

      if (layout) {
        layoutNavigationState.set(layout);
      }

      if (t) {
        // The workspace expects the timestamp to be encoded as a fixed-point seconds value.
        searchParams.set(SearchParams.TIMESTAMP, nanoSecToSecStr(t));
      }
    }

    window.scrollTo(0, 0); // Ensure the page doesn't load scrolled down
    this.#navigate(`${Goto.route.Visualize}?${searchParams}`);
  }

  public secretsList() {
    this.#navigate(Goto.route.SecretsList);
  }
}

export type PageRoute = (typeof Goto.route)[keyof typeof Goto.route];

export type ExternalRoute = `https://${string}`;
